import { LitElement, html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { voyageCursorStyles } from "../../voyage-style/src/voyage-style";
import "./voyage-view-history";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import "../../voyage-tooltip/src/voyage-tooltip-proxy";
import "./breadcrumb-navigation";
import { AuthorizationRoles, AuthService} from "../../../src/services/auth-service";
import { VesselsModel } from "../../../src/models/vessels-model";
import { _convertElementToKt, _convertElementToMs, _emptyCompanyLimit, _isTEU, _renderEmptyPage, extractChangeData, _shouldConvertUnit } from "./util";
import { voyageWeatherLmitsStyles } from "./weather-limits-styles";
import { ELEMENT_DATA, HeavyWeatherLimitElement, wavesMinValues, wind50MMinValues, CustomVessel} from "../heavy-weather-limits-model";
import { TypeAHeadOption } from "../../voy-input-type-a-head/src/voy-input-type-a-head";
import { VesselVoyageVersion } from "../../voy-global-filters/src/types";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import "@maersk-global/mds-components-core/mc-toast";
import "@maersk-global/mds-components-core/mc-notification";
import "@maersk-global/mds-components-core/mc-icon";

@customElement("voyage-custom-weather-limits")
export class VoyageHeavyWeatherAlarmLimits extends LitElement {
  @property({ type: Boolean, reflect: true }) shown: boolean = false;
  @property({ type: String }) imo = "";
  @property({ type: Boolean }) isEditView: boolean = true;
  @property({ type: Array }) private _changesData: any = [];
  @property({ attribute: false }) private _isValid: boolean = true;
  @property({ type: Boolean }) hasWriteAccess: boolean = AuthService.userHasRole(AuthorizationRoles.WeatherLimitWrite);
  @property({ type: Boolean }) hasReadAccess: boolean = AuthService.userHasRole(AuthorizationRoles.WeatherLimitRead);
  @property({ type: String }) newVesselImo: string = "";
  @property({ type: Array }) _isValidInput: string[] = [];

  @state() private _isEditing: boolean = false;
  @state() private _isUpdated: boolean = false;
  @state() private _viewingHistory: boolean = false;
  @state() private _showModal: boolean = false;
  @state() _loading = true;
  @state() private _selectedVessels: any = [];
  @state() private _deleteConformation: boolean = false;
  @state() private _showDeletedToast: boolean = false;

  private _unit: string = "m/s";
  private _vesselForDeletion: CustomVessel | {} = {};
  private _oldWeatherLimitsData:CustomVessel[] = [];
  private _elements: HeavyWeatherLimitElement[];

  private maerskVesselList =
    VesselsModel!.globalSearchMaerskVesselsMetaData.filter(
      (item: any) =>
        item.vesselVersion === VesselVoyageVersion.Cloud ||
        item.vesselVersion === VesselVoyageVersion.Premium
    );
  private _history: Array<{
    Timestamp: string;
    ModifiedBy: string;
    FieldModified: any;
    From: any;
    To: any;
  }> = [];

  @state() private vesselList: TypeAHeadOption[] = this.maerskVesselList.map(
    (item, index) => ({
      label: String(item.vesselName ?? ""),
      value: String(item.imo ?? ""),
    })
  );

  constructor() {
    super();
    this._elements = ELEMENT_DATA;

    this._onClose = this._onClose.bind(this);
    this._onAccept = this._onAccept.bind(this);
    this._onEditFields = this._onEditFields.bind(this);
    this._onViewHistory = this._onViewHistory.bind(this);
    this._onCancel = this._onCancel.bind(this);
  }

  static get styles() {
    return [voyageCursorStyles, voyageWeatherLmitsStyles];
  }

  //Note: Replace below with the API implementation
  @state() private _vessels: CustomVessel[] =[]
    

  async connectedCallback() {
    super.connectedCallback();
    this._loading = true;
    const customWeatherLimitsValue = await VoyFleetAlarmModel!.getCustomWeatherLimits();
    this._vessels = customWeatherLimitsValue.map((vessel: any) => ({
      imo: vessel.imo,
      name: VesselsModel!.getVesselName(Number(vessel.imo)) || vessel.imo,
      maerskPolicy: vessel.company_limits || JSON.parse(JSON.stringify(_emptyCompanyLimit)),
      customLimits: vessel.custom_weather_limit,
    })) ;

    customWeatherLimitsValue.forEach(({ custom_weather_limit }: { custom_weather_limit: Record<string, number | null> }) => {
      const limits: Record<string, number | null> = {};

      Object.entries(custom_weather_limit).forEach(([key, value]) => {
        if (typeof value === "number" || value === null) {
          limits[key] = value;
        }
      });
            this._oldWeatherLimitsData =  JSON.parse(JSON.stringify(this._vessels));
    });

    this._loading = false;
    this.ensureVesselExists();
  }

  updated(changedProps: Map<string, any>) {
    if (changedProps.has("isEditView")) {
      this._isEditing = this.isEditView;
    }
  }

  private async ensureVesselExists() {
    const exists = this._vessels.some((v: CustomVessel) => v.imo === Number(this.imo));
    if (!exists && this.imo) {
       const reponse = await VoyFleetAlarmModel!.getCustomWeatherLimitsByIMO([Number(this.imo)]);
      const newVessel = {
        imo: Number(this.imo),
        name: VesselsModel!.getVesselName(Number(this.imo)) || this.imo,
        maerskPolicy: reponse[0]?.company_limits || JSON.parse(JSON.stringify(_emptyCompanyLimit)),
        customLimits: reponse[0]?.custom_weather_limit || JSON.parse(JSON.stringify(_emptyCompanyLimit)),
      };
      this._vessels.unshift(newVessel);
      this.requestUpdate();
      this._oldWeatherLimitsData = JSON.parse(JSON.stringify([
        ...this._oldWeatherLimitsData,
        newVessel
      ]));
    }
  }

  private _handleUnitChange(event: any) {
    const newUnit = event.target.id;
    if (this._unit === newUnit) return;
    this._unit = newUnit;

    if (newUnit === "kt") {
      this._convertToKt();
    } else if (newUnit === "m/s") {
      this._convertToMs();
    }

    this.requestUpdate();
  }


  private _goToCustomView() {
    this._onClose();
    const showCustomLimit = new CustomEvent(
      "open-heavy-weather-comapny-limits",
      {
        bubbles: true,
        composed: true,
      }
    );
    VesselsModel!.dispatchEvent(showCustomLimit);
  }

   private async  _addNewVesselToList() {
    if (this._selectedVessels.length === 0) {
      this._showModal = false;
      return;
    }
    const existingVessels = this._vessels.map((vessel) => Number(vessel.imo));

    this._selectedVessels = this._selectedVessels.filter(
      (vessel: TypeAHeadOption) => !existingVessels.includes(Number(vessel.value) ?? "")
    );

   let imoList = this._selectedVessels.map((vessel: TypeAHeadOption) => Number(vessel.value) ?? "");
    const reponse = await VoyFleetAlarmModel!.getCustomWeatherLimitsByIMO(imoList)
    const newVessels = this._selectedVessels.map((vessel: TypeAHeadOption) => ({
      imo: vessel.value ?? "",
      name: vessel.label ?? "",
      maerskPolicy: reponse.find((item: any) => item.imo === Number(vessel.value))?.company_limits || JSON.parse(JSON.stringify(_emptyCompanyLimit)),
      customLimits:  reponse.find((item: any) => item.imo === Number(vessel.value))?.custom_weather_limit ||JSON.parse(JSON.stringify(_emptyCompanyLimit)),
    }));

    this._oldWeatherLimitsData = JSON.parse(JSON.stringify([
      ...this._oldWeatherLimitsData,
      ...newVessels
    ]));

    this._vessels.unshift(...newVessels);
    this._isEditing = true;
    this._showModal = false;
    this.requestUpdate();
    this._selectedVessels = [];
  }

  render() {
    const paths = [
      { name: "Home page", action: this._onClose.bind(this) },
      { name: "Custom Limits", action: this._onBackFromHistory.bind(this) },
    ];

    if (this._viewingHistory) {
      paths.push({
        name: "Changes history",
        action: this._onViewHistory.bind(this),
      });
    }

    this._checkValidity();

    return html`
      <div class="container">
        <div class="header-container">
          <h1 class="header">
            ${this._viewingHistory
              ? "Custom weather alarm limits: Changes history"
              : "Custom weather alarm limits"}
          </h1>
          <div class="view-switch">
            <mc-button
              appearance="primary"
              variant="filled"
              fit="small"
              @click=${this._goToCustomView}
              >Switch to Maersk's weather limits</mc-button
            >
          </div>
        </div>

        ${this._renderBreadcrumb(paths)}
        ${this._viewingHistory
          ? this._renderHistory()
          : html`
              ${this._loading
                ? html`
                    <div class="spinner">
                      <mc-loading-indicator
                        fit="large"
                        label="Loading data..."
                      ></mc-loading-indicator>
                    </div>
                  `
                : html`
                    <div class="radio-button-display">
                      <div class="fab-container ">
                        ${this._showModal
                          ? html`${this._renderAddVesselModal()}`
                          : null}
                        <button
                          class="fab"
                          @click=${() => (this._showModal = true)}
                        >
                          +
                        </button>
                      </div>
                      ${this._renderConfirmationModal()}
                      ${this._showDeletedToast ? this._renderToast() : ""}

                      <div class="padding">Change wind unit:</div>
                      <div class="padding">
                        <mc-radio
                          id="m/s"
                          name="windUnit"
                          label="m/s"
                          @change=${(evt: InputEvent) =>
                            this._handleUnitChange(evt)}
                          ?checked=${this._unit == "m/s"}
                        />
                      </div>
                      <div class="padding">
                        <mc-radio
                          id="kt"
                          name="windUnit"
                          label="kt"
                          @change=${(evt: InputEvent) =>
                            this._handleUnitChange(evt)}
                          ?checked=${this._unit == "kt"}
                        />
                      </div>
                    </div>

                    <!-- Limits Table -->
                    ${this._vessels && this._vessels.length > 0 ? html`
                    <div class="table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th class="sticky-col1 sticky-header">
                              Vessel Name
                            </th>
                            <th class="sticky-col2 sticky-header">IMO</th>
                            <th class="sticky-col3 sticky-header">Limits</th>
                            ${this._elements.map((element) => {
                              const unit = this._unit;
                              const shouldConvert =_shouldConvertUnit(
                                element.name
                              );
                              return html`
                                <th
                                  class=${element.connectedTo
                                    ? "connected"
                                    : "name"}
                                >
                              ${element.connectedTo ? `${element.connectedToText} ${element.text}` : element.text}
                                  ${shouldConvert
                                    ? `(${unit})`
                                    : `${element.unit}`}
                                </th>
                              `;
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          ${this._vessels.map(
                            (vessel: CustomVessel) => html`
                              <tr class="group-header">
                                <td
                                  rowspan="2"
                                  class="name sticky-col1 right-border"
                                >
                                  <span>${vessel.name}</span>
                                  ${this._isEditing
                                    ? html`<span
                                        @click=${() => {
                                          //Replace below with the API call to delete vessel
                                          this._deleteConformation = true;
                                          this._vesselForDeletion = vessel;
                                        }}
                                        ><mc-icon
                                          icon="trash"
                                          color="red"
                                        ></mc-icon
                                      ></span>`
                                    : ""}
                                </td>
                                <td
                                  rowspan="2"
                                  class="name sticky-col2 right-border"
                                >
                                  ${vessel.imo}
                                </td>
                                <td class="policy-header sticky-col3">
                                  Maersk Policy
                                </td>
                                ${this._elements.map(
                                  (element) => html`
                                    <td class="limits-non-editable">
                                      ${this._getThresholdValue(
                                        vessel,
                                        element
                                      )}
                                    </td>
                                  `
                                )}
                              </tr>
                              <tr class="group-data">
                                <td class="sticky-col3">Custom Limits</td>
                                ${this._elements.map(
                                  (element) => html`
                                    <td>
                                      ${this._renderInputElement(
                                        element,
                                        vessel
                                      )}
                                    </td>
                                  `
                                )}
                              </tr>
                            `
                          )}
                        </tbody>
                      </table>
                    </div> `: html`
                    <!-- <div> Click on + icon and add vessel to custmoise weather limits </div> -->
                     ${_renderEmptyPage("Click on + icon and add vessel to custmoise weather limits")}
                    `}

                    <!-- Status Messages -->
                    ${this._isUpdated
                      ? html`<div class="footer-text">
                          Table data has been updated.
                        </div>`
                      : ""}
                    ${this._isValid
                      ? ""
                      : html`<div class="footer-text-invalid">
                          Please make sure there are no errors before clicking
                          Apply.
                        </div>`}
                  `}
            `}

           ${this._vessels && this._vessels.length > 0 ? html`<slot name="footer"> ${this._renderFooter()} </slot>`: ""}
      </div>
    `;
  }

  private _renderFooter() {
    return html`
      <div id="footer" class="button-container">
        ${this._isEditing && this.hasWriteAccess
          ? html`
              <button class="footerButton cancel" @click=${this._onViewHistory}>
                View history
              </button>
              <button class="footerButton cancel" @click=${this._onCancel}>
                Cancel
              </button>
              <button
                class="footerButton accept ${!this._isValid ? "disabled" : ""}"
                ?disabled=${!this._isValid || !this.hasWriteAccess}
                @click=${this._onAccept}
              >
                Apply
              </button>
            `
          : html`
              <button class="footerButton cancel" @click=${this._onViewHistory}>
                View history
              </button>
              <button
                class="footerButton cancel ${!this.hasWriteAccess
                  ? "disabled"
                  : ""}"
                ?disabled=${!this.hasWriteAccess}
                @click=${this._onEditFields}
              >
                Edit fields
              </button>
              <button
                class="footerButton accept disabled"
                ?disabled=${this._isEditing}
              >
                Apply
              </button>
            `}
      </div>
    `;
  }
  private _renderHistory() {
    return html`
      <voyage-view-history
        .history=${this._history}
        .customHistory=${true}
        @back=${this._onBackFromHistory}
      >
      </voyage-view-history>
    `;
  }

  private _renderBreadcrumb(paths: any[]) {
    return html`
      <div class="breadcrumb-container">
        <breadcrumb-navigation .paths=${paths}></breadcrumb-navigation>
      </div>
    `;
  }

  private _getThresholdValue(
    vessel: { name: string; imo: number; maerskPolicy: any; customLimits: any },
    element: HeavyWeatherLimitElement
  ): string | number {
    const field = element.name;
    const policyValue = vessel.maerskPolicy?.[field];

    const isPeriodField = field === "HeadWavesPeriod" || field === "FollowingWavesPeriod" || field === "BeamWavesPeriod";
    const shouldConvert =_shouldConvertUnit(field) && this._unit === "kt";
    const isDropdownField = element.dropdown && element.dropdown.length > 0;
    if(isDropdownField) {
      if (Array.isArray(element.dropdown)) {
        const selectedValue = vessel.maerskPolicy[field];
        const selectedOption = element.dropdown.find(opt => opt.value === selectedValue);
        return selectedOption?.text || "--";
      }
    }
    if (isPeriodField) {
      const minVal = policyValue?.min;
      const maxVal = policyValue?.max;
      return `from: ${minVal ?? "--"} to ${maxVal ?? "--"}`;
    }

    if (shouldConvert) {
      const converted =
        policyValue != null
          ? parseFloat((policyValue * 1.94384).toFixed(1))
          : "--";
      return converted;
    }

    return policyValue ?? "--";
  }

  private _renderInputElement(element: HeavyWeatherLimitElement, vessel: any) {
    if (element.dropdown) {
      return this._renderDropdownInput(element, vessel);
    } else if (element.connectedTo) {
      return this._renderPeriodInput(element, vessel);
    } else {
      return this._renderNumberInput(element, vessel);
    }
  }

  private _renderNumberInput(element: HeavyWeatherLimitElement, vessel: any) {
    const min = 0
    const prop = element.name;
    const imoNumber = vessel.imo;
    let max: number | undefined;
    max = vessel.maerskPolicy[prop] || element.max;

    if (
      this._unit === "kt" &&
      (prop === "Wind10M" ||
        prop === "Wind50M" ||
        prop === "WindGusts10M" ||
        prop === "WindGusts50M" ||
        prop === "RiskWindSpeed")
    ) {
      max = parseFloat((max! * 1.94384).toFixed(1));
    }
    const value = vessel.customLimits[prop];

    const onValueInput = (evt: InputEvent) => {
      const target = <HTMLInputElement>evt.target;
      const rawValue = target.value.trim();
      const inputValue = rawValue === "" ? null : parseFloat(rawValue);

      vessel.customLimits[prop] = inputValue;

      const propValue = vessel.customLimits[prop];

      this._updateChangesData(element, inputValue, imoNumber);
      this._validateNumberInput(element, inputValue, min, max, imoNumber);
    };

    const minCondition = `${element.name}+${imoNumber}+min`;
    const maxCondition = `${element.name}+${imoNumber}+max`;

    return html`
      <div class="number-input">
        <input
          data-test=${element.name}
          class="cell numberInput"
          type="number"
          step="0.1"
          min=${min}
          max="${max ?? Infinity}"
          placeholder="Enter value"
          @input=${onValueInput}
          ?disabled=${!this._isEditing}
          .value="${value !== null && value !== undefined ? value : ""}"
        />
        ${this._isValidInput.includes(minCondition)
          ? html`
              <div class="number-validation-icon">
                <span class="questionCircle"
                  >${SvgIcons.customLightQuestionCircle}</span
                >
                <voyage-tooltip-proxy
                  text="Minimum value is ${min}"
                  html-content
                  .positionPriority=${["top"]}
                ></voyage-tooltip-proxy>
              </div>
            `
          : html`<voyage-tooltip
              text="Minimum value is ${min}"
              html-content
              .positionPriority=${["top"]}
            ></voyage-tooltip>`}
        ${this._isValidInput.includes(maxCondition)
          ? html`
              <div class="number-validation-icon">
                <span class="questionCircle"
                  >${SvgIcons.customLightQuestionCircle}</span
                >
                <voyage-tooltip-proxy
                  text="Maximum value is ${max}"
                  html-content
                  .positionPriority=${["top"]}
                ></voyage-tooltip-proxy>
              </div>
            `
          : html``}
      </div>
    `;
  }

  private _renderPeriodInput(element: HeavyWeatherLimitElement, vessel: any) {
    const prop = element.name;
    let imoNumber = vessel.imo;
    let max: number | undefined;

    max = vessel.maerskPolicy[prop]?.max;
    const onValueInput = (evt: InputEvent, type: "min" | "max") => {
      const target = evt.target as HTMLInputElement;
      const value = target.value !== "" ? Number(target.value) : null;
      if (vessel?.customLimits?.[prop]) {
        vessel.customLimits[prop][type] = value;
      }

      this._updateChangesDataForPeriodInput(element, value, type, imoNumber);
      this._validatePeriodInput(value, element, type, max, imoNumber);

      this.requestUpdate();
    };

    return html`
      <div class="cell numberInput periodInput" connected>
        <div>from</div>
        <div class="number-input">
          <input
            data-test=${element.name + "-from"}
            class="inlineInput"
            type="number"
            step="1"
            min="0"
            max=${vessel.customLimits?.[prop]?.max}
            placeholder="Enter value"
            @input=${(e: InputEvent) => onValueInput(e, "min")}
            ?disabled=${!this._isEditing}
            value=${vessel.customLimits?.[prop]?.min ?? ""}
          />
          ${this._isValidInput.includes(`${element.name}+min+${imoNumber}`)
            ? html`
                <div>
                  <span class="questionCircle"
                    >${SvgIcons.customLightQuestionCircle}</span
                  >
                  <div>
                    <voyage-tooltip-proxy
                      text="'from' value is more than 'to' value"
                      html-content
                      .positionPriority=${["left"]}
                    >
                    </voyage-tooltip-proxy>
                  </div>
                </div>
              `
            : html``}
        </div>

        <div>to</div>
        <div class="number-input">
          <input
            data-test=${element.name + "-to"}
            class="inlineInput size"
            type="number"
            step="1"
            min=${vessel.customLimits?.[prop]?.min ?? 0}
            max="${max ?? Infinity}"
            placeholder="Enter value"
            @input=${(e: InputEvent) => onValueInput(e, "max")}
            ?disabled=${!this._isEditing}
            value=${vessel.customLimits?.[prop]?.max ?? ""}
          />
          ${this._isValidInput.includes(`${element.name}+max+${imoNumber}`)
            ? html`
                <div class="number-validation-icon">
                  <span class="questionCircle"
                    >${SvgIcons.customLightQuestionCircle}</span
                  >
                  <voyage-tooltip-proxy
                    text="Maximum value is ${max}"
                    html-content
                    .positionPriority=${["top"]}
                  >
                  </voyage-tooltip-proxy>
                </div>
              `
            : html``}
        </div>
      </div>
    `;
  }

  private _renderDropdownInput(element: HeavyWeatherLimitElement, vessel: any) {
    const prop = element.name;

    const onValueInput = (evt: InputEvent) => {
      const target = evt.target as HTMLSelectElement;
      const value = target.value === "" ? null : Number(target.value);

      if (!vessel?.customLimits) return;

      vessel.customLimits[prop] = value;

      const selectedOption = element.dropdown?.find(
        (option) => option.value === value
      );
      const text = selectedOption ? selectedOption.text : null;

      this._updateChangesData(element, text, vessel.imo);
      this.requestUpdate(); // Optional: ensure reactive update if needed
    };

    const selectedValue = vessel.customLimits?.[prop];

    return html`
      <select
        data-test=${element.name + "-warn"}
        class="cell dropdown"
        @input=${onValueInput}
        ?disabled=${!this._isEditing}
      >
        <option value="">Select</option>
        ${element.dropdown?.map(
          ({ value, text }) => html`
            <option value=${value} ?selected=${value === selectedValue}>
              ${text}
            </option>
          `
        )}
      </select>
    `;
  }

  private _renderVessel(vessel: TypeAHeadOption): TemplateResult<1> {
    return html`<div class="tp-service-option">${vessel.label}</div>`;
  }

  private _renderPill(name: string): TemplateResult {
    return html`<div class="pill">
      <span>${name}</span
      ><button class="pill-close-btn" @click=${() => this._removePill(name)}>
        ${SvgIcons.lightTimes}
      </button>
    </div>`;
  }

  private _renderAddVesselModal() {
    return html`
      <mc-modal
        heading="Enter IMO or Vessel Name"
        open
        height="350px"
        width="500px"
        :zindex="99999"
        backdropcloseactiondisabled
        entercloseactiondisabled
        @closed=${() => (this._showModal = false)}
      >
        <voy-input-type-a-head
          placeholder="Search imo"
          .options=${this.vesselList}
          .showLabel=${true}
          .renderItemu=${this._renderVessel}
          @change=${(e: any) => {
            this._selectedVessels.push(e.detail);
            this.requestUpdate();
          }}
        ></voy-input-type-a-head>
        ${this._selectedVessels.length
          ? html`<div class="services-pills-wrapper">
              ${this._selectedVessels.map((vessel: any) =>
                this._renderPill(vessel.label)
              )}
            </div> `
          : ""}

        <mc-button
          slot="primaryAction"
          @click=${() => this._addNewVesselToList()}
          appearance="primary"
          fit="medium"
          dialogaction="ok"
          >Add</mc-button
        >
      </mc-modal>
    `;
  }

  private _renderConfirmationModal() {
    return html`
      <mc-modal
        heading=${`Delete entry for ${
          "name" in this._vesselForDeletion
            ? (this._vesselForDeletion as CustomVessel).name
            : ""
        }`}
        ?open=${this._deleteConformation}
        height="250px"
        width="500px"
        :zindex="99999"
        backdropcloseactiondisabled
        entercloseactiondisabled
        @closed=${() => (this._deleteConformation = false)}
      >
        <div>
          Cstomised values will be removed from the system, and weather limits
          will be default values based on TEU capacity. Please confirm if you want to
          proceed?
        </div>
        <mc-button
          slot="secondaryAction"
          appearance="neutral"
          fit="medium"
          dialogaction="cancel"
          >Cancel</mc-button
        >
        <mc-button
          slot="primaryAction"
          @click=${() => {
            if (
              "imo" in this._vesselForDeletion &&
              this._vesselForDeletion.imo
            ) {
              this._deleteEntry(this._vesselForDeletion.imo);
            }
          }}
          appearance="primary"
          fit="medium"
          dialogaction="ok"
          >Confirm</mc-button>
        </mc-toast>
      </mc-modal>
    `;
  }

  private _renderToast() {
    return html`
      <mc-toast
        open
        appearance="success"
        position="bottom-right"
        @close=${() => {
          this._showDeletedToast = false;
          this.requestUpdate();
        }}
      >
        <mc-notification
          appearance="success"
          body="Table updated, entry got removed from custom limits"
        ></mc-notification>
      </mc-toast>
    `;
  }

  private _onClose() {
    this.dispatchEvent(new Event("cancel"));
    this._isEditing = false;
  }

  private _onCancel() {
    this._isEditing = false;
    this._isUpdated = false;
  }

  private async _onAccept() {
    if (this._isValidInput.length > 0) return;
    const convertedValuesHeavyWeatherDataMs = this._vessels.map(
      (vessel: CustomVessel) => ({
        imo: vessel.imo,
        customLimits: { ...vessel.customLimits },
      })
    );
    // convert to m/s

    convertedValuesHeavyWeatherDataMs.map((vessel: any) => {
      const customLimits = vessel.customLimits as Record<string, any>;
      for (const key in customLimits) {
        const value = customLimits[key];

        if (
          this._unit === "kt" &&
         _shouldConvertUnit(key) &&
          value !== null &&
          typeof value === "number"
        ) {
          (vessel.customLimits as Record<string, any>)[key] = parseFloat(
            (value * 0.514444).toFixed(1)
          ); // Convert kt → m/s
        }
      }
    });
    const changesData = extractChangeData(this._oldWeatherLimitsData, convertedValuesHeavyWeatherDataMs)
    const payload = convertedValuesHeavyWeatherDataMs.map((vessel: any) => ({
      imo: Number(vessel.imo),
      custom_weather_limit: {...vessel.customLimits,type:"custom"},
      changes_data: changesData[vessel.imo] || null,
      user_data: {
        name: AuthService.getCurrentUserdisplayName(),
        email: AuthService.getCurrentUserId()
      }
    })).filter(item => item.changes_data !== null);;
   
    await VoyFleetAlarmModel!.setCustomWeatherLimits(payload); // note: API call to set custom weather limits
    this.dispatchEvent(new Event("accept"));
    this._isEditing = false;
    this._isUpdated = true;
    this._changesData = [];

  }

  private _convertToKt(): void {
    this._elements = this._elements.map((element) =>
      _convertElementToKt(element)
    );

    const vesselsToConvert = [...this._vessels];

    vesselsToConvert.forEach((vessel: CustomVessel) => {
      const customLimits = vessel.customLimits as Record<string, any>;
      // Convert custom limits from m/s to kt
      for (const key in customLimits) {
        const value = customLimits[key];
        if (
         _shouldConvertUnit(key) &&
          typeof value === "number" &&
          value !== null
        ) {
          customLimits[key] = parseFloat((value * 1.94384).toFixed(1)); // m/s → kt
        }
      }
    });
  }

  private _convertToMs() {
    this._elements = this._elements.map((element) =>
      _convertElementToMs(element)
    );
    const vesselsToConvert = [...this._vessels];
    vesselsToConvert.forEach((vessel: CustomVessel) => {
      const customLimits = vessel.customLimits as Record<string, any>;

      // Convert custom limits from m/s to kt
      for (const key in customLimits) {
        const value = customLimits[key];
        if (
         _shouldConvertUnit(key) &&
          typeof value === "number" &&
          value !== null
        ) {
          customLimits[key] = parseFloat((value * 0.514444).toFixed(1)); // m/s → kt
        }
      }
    });
  }


  private _updateChangesDataForPeriodInput(
    element: HeavyWeatherLimitElement,
    value: number | null,
    type: "min" | "max",
    imoNumber: string
  ) {
    const prop = element.name;
    const elementName = `${element.connectedToText} ${element.text} ${element.unit} / ${type}`;
  }

  private _validatePeriodInput(
    value: number | null,
    element: HeavyWeatherLimitElement,
    type: "min" | "max",
    max: number | undefined,
    imo?: string
  ) {
    if (value !== null && (value < 0 || value > (max ?? Infinity))) {
      this._isValidInput.push(`${element.name}+${type}+${imo}`);
    } else {
      const index = this._isValidInput.indexOf(
        `${element.name}+${type}+${imo}`
      );
      if (index > -1) {
        this._isValidInput.splice(index, 1);
      }
    }
  }

  private _updateChangesData(
    element: HeavyWeatherLimitElement,
    value: number | string | null,
    imo: string
  ) {
    const prop = element.name;
    const unit = this._unit;
    const shouldConvert =_shouldConvertUnit(element.name);
    const elementName = `${element.text} ${
      shouldConvert && element.unit !== undefined
        ? `(${unit})`
        : `${element.unit}`
    }`;

    // const oldLimits = this._oldWeatherLimitsData?.[imo]?.[prop];
    const isElementExist = this._changesData.findIndex(
      (item: any) => item.name === elementName && item.imo === imo
    );

  }

  private _validateNumberInput(
    element: HeavyWeatherLimitElement,
    value: number | null,
    min: number,
    max: number | undefined,
    imo?: string
  ) {
    const minCondition = `${element.name}+${imo}+min`;
    const maxCondition = `${element.name}+${imo}+max`;

    if (value !== null) {
      if (value < min) {
        if (!this._isValidInput.includes(minCondition)) {
          this._isValidInput = [...this._isValidInput, minCondition];
        }
      } else {
        this._isValidInput = this._isValidInput.filter(
          (item) => item !== minCondition
        );
      }

      if (value > (max ?? Infinity)) {
        if (!this._isValidInput.includes(maxCondition)) {
          this._isValidInput = [...this._isValidInput, maxCondition];
        }
      } else {
        this._isValidInput = this._isValidInput.filter(
          (item) => item !== maxCondition
        );
      }
    } else {
      this._isValidInput = this._isValidInput.filter(
        (item) => item !== minCondition && item !== maxCondition
      );
    }
  }

  private _onEditFields() {
    this._isEditing = !this._isEditing;
  }

  private _onViewHistory() {
    this._viewingHistory = true;
  }

  private _onBackFromHistory() {
    this._viewingHistory = false;
  }

  private async _checkValidity() {
    await this.updateComplete;

    const inputs = <NodeListOf<HTMLInputElement>>(
      this.shadowRoot!.querySelectorAll(`input[type="number"]`)
    );
    let isValid = true;
    for (const input of inputs) {
      if (!input.validity.valid) {
        isValid = false;
        break;
      }
    }
    this._isValid = isValid;
  }


  private _deleteEntry(imo:number) {
    VoyFleetAlarmModel!.deleteCustomWeatherLimits(imo);
    this._vessels = this._vessels.filter((v) => v.imo !== imo);
    this.requestUpdate();
    this._showDeletedToast = true;
  }


  private _removePill(name: string) {
       this._selectedVessels = this._selectedVessels.filter((item: { label: string; }) => item.label !== name);
       this.requestUpdate();
  }

}
